import {
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { untilDestroyed } from '@ngneat/until-destroy';
import { LogoLoaderComponent } from '@y42/core/logo-loader';
import { fromResize } from '@y42/rxjs';
import { merge, Subject, tap } from 'rxjs';
import { MonacoEditorLoader } from './monaco-loader';

export const MONACO_SETTINGS: monaco.editor.IStandaloneEditorConstructionOptions = {
  glyphMargin: false,
  folding: false,
  contextmenu: false,
  fontFamily: '"Fira Code", monospace',
  fontSize: 12,
  formatOnPaste: true,
  minimap: {
    enabled: false,
  },
  fixedOverflowWidgets: true,
  scrollbar: {
    alwaysConsumeMouseWheel: false,
  },
  automaticLayout: true,
  scrollBeyondLastLine: false,
  theme: 'vs',
};

const DEFAULT_LANGUAGE_ID = 'plaintext';
export const LANGUAGE_ID_ALIASES = {
  yml: 'yaml',
};

@Directive()
export abstract class MonacoEditorBase<Editor extends monaco.editor.IEditor> implements OnInit, OnDestroy {
  @Input() fileName!: string | undefined;

  @Input() languageWorkerFactory!: { [language: string]: () => Worker };

  @Output() editorInitialized = new EventEmitter<Editor>();

  protected editor!: Editor;

  protected readonly monacoLoader = inject(MonacoEditorLoader);

  protected readonly vcRef = inject(ViewContainerRef);

  protected readonly host = inject(ElementRef);

  protected readonly layout$ = new Subject<void>();

  private logoRef: ComponentRef<LogoLoaderComponent> | null = null;

  get languageId() {
    if (!this.fileName) {
      return DEFAULT_LANGUAGE_ID;
    }

    const fileExt = this.fileName.split('.').pop();
    if (!fileExt) {
      return DEFAULT_LANGUAGE_ID;
    }

    return LANGUAGE_ID_ALIASES[fileExt as keyof typeof LANGUAGE_ID_ALIASES] || fileExt;
  }

  abstract init(): Promise<void>;

  async ngOnInit() {
    this.logoRef = this.vcRef.createComponent(LogoLoaderComponent);
    this.logoRef.location.nativeElement.classList.add('center-absolute', 'z-10');
    this.host.nativeElement.classList.add('relative');
    this.host.nativeElement.appendChild(this.logoRef.location.nativeElement);
    await this.monacoLoader.load();
    if (this.languageWorkerFactory) {
      for (const [language, workerFactory] of Object.entries(this.languageWorkerFactory)) {
        this.monacoLoader.registerWorker(language, workerFactory);
      }
    }

    const font = `${MONACO_SETTINGS.fontSize}px ${MONACO_SETTINGS.fontFamily}`;
    if (!document.fonts.check(font)) {
      await document.fonts.load(font);
      // Fix renderWhitespace with custom font
      // https://github.com/Microsoft/monaco-editor/issues/648#issuecomment-564978560
      monaco.editor.remeasureFonts();
    }

    await this.init();

    this.editorInitialized.emit(this.editor);
    this.logoRef.destroy();
    this.logoRef.location.nativeElement.remove();
    merge(fromResize(this.host.nativeElement, { omitDebounce: true }), this.layout$)
      .pipe(
        tap(() => this.editor.layout()),
        untilDestroyed(this),
      )
      .subscribe();
  }

  updateLayout() {
    this.layout$.next();
  }

  ngOnDestroy() {
    this.editor?.dispose();
    this.logoRef?.destroy();
  }
}
